import * as React from "react";
import { cn } from "@/lib/cn";
import { Skeleton } from "./Feedback";
import { IconTile } from "./IconTile";
import { InfoHint } from "./InfoHint";

/**
 * A labelled figure. The console's only summary primitive. Appearance is
 * `.stat` in `styles/ui.css`, where three rules live together:
 *
 * **The value is Inter, not the display face.** Barlow Condensed carries the
 * headings on every other surface, and a big number set in it reads as
 * decoration rather than as data. Figures are proportional here deliberately -
 * `tabular-nums` gives every digit the width of a zero, which makes a standalone
 * `121` look gapped at 30px. Tabular belongs in table columns, and `Table` does
 * that.
 *
 * **The number never carries colour.** Colour in this product means severity,
 * and a teal `12` implies a state that does not exist. Anything that genuinely
 * has a state gets a `Badge`, which carries an icon and a word as well as a hue.
 *
 * **`tooltip` is opt-in, not a caption.** A subtitle under every tile read as
 * noise once there were eight of them on one page - most labels ("Visits",
 * "Scheduled today") already say what they count. Reach for it only when the
 * figure genuinely needs unpacking ("Open findings": hazards raised with no
 * control logged against them is not obvious from the label alone).
 */
export function Stat({
  label,
  value,
  tooltip,
  icon,
  loading = false,
  className,
}: {
  label: string;
  value: React.ReactNode;
  /** Explains the figure on hover/focus, via `InfoHint`. Leave unset for a self-explanatory tile. */
  tooltip?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("stat", className)}>
      <div className="stat__top">
        <span className="info-hint-row">
          <p className="stat__label">{label}</p>
          {tooltip && <InfoHint text={tooltip} />}
        </span>
        {icon && <IconTile size="sm">{icon}</IconTile>}
      </div>

      {loading ? (
        <Skeleton className="stat__skeleton" />
      ) : (
        // A placeholder is dimmed and stepped down. An em dash at the figure's
        // full 38px is a thick horizontal bar that reads as a divider or a
        // half-loaded skeleton rather than as "no number here".
        <p className={cn("stat__value", typeof value !== "number" && "stat__value--placeholder")}>
          {value}
        </p>
      )}
    </div>
  );
}
